import {Component, inject, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import {AppState} from "../../../../store/app.state";
import {
    selectUserFileShareIsDownloading,
    selectUserFileShareIsLoading, selectUserFileShareIsUploading,
    selectUserFileShareList,
    selectUserUploadedFileCount
} from "../../../../store/user/user.selectors";
import {AsyncPipe, DatePipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {downloadFile, loadFiles, uploadFile} from "../../../../store/user/user.actions";
import LoaderComponent from "../../../../components/loader/loader.component";
import {AppointmentCardComponent} from "../../../../components/appointment-card/appointment-card.component";
import {TranslocoPipe} from "@ngneat/transloco";
import {FileShareInfo, UploadedFileType} from "../../../../models/file-share.models";
import {FileShareService} from '../../../../services/file-share.service';
import {LogService} from '../../../../services/log.service';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {combineLatest} from "rxjs";

interface UploadForm {
    file: FormControl<File | null>;
    type: FormControl<string | null>;
}

@Component({
    selector: 'app-file-share-page',
    standalone: true,
    imports: [
        NgForOf,
        AsyncPipe,
        LoaderComponent,
        NgIf,
        AppointmentCardComponent,
        TranslocoPipe,
        ReactiveFormsModule,
        DatePipe,
        NgClass
    ],
    templateUrl: './file-share-page.component.html',
    styleUrl: './file-share-page.component.css'
})
export class FileSharePageComponent implements OnInit {
    #store = inject(Store<AppState>);
    #fileShareService = inject(FileShareService);
    #logService = inject(LogService);
    #fb = inject(FormBuilder);

    fileShareIsLoading$ = this.#store.select(selectUserFileShareIsLoading);
    fileList$ = this.#store.select(selectUserFileShareList);
    userUploadedFileCount$ = this.#store.select(selectUserUploadedFileCount);
    fileIsDownloading$ = this.#store.select(selectUserFileShareIsDownloading);
    fileIsUploading$ = this.#store.select(selectUserFileShareIsUploading);

    fileTypeList: UploadedFileType[] = [];
    maxNumFiles = 0;
    uploadForm: FormGroup<UploadForm>;

    constructor() {
        this.uploadForm = this.#fb.group<UploadForm>({
            file: this.#fb.control(null, [Validators.required]),
            type: this.#fb.control(null, [Validators.required])
        });
    }

    ngOnInit() {
        this.#store.dispatch(loadFiles());
        combineLatest([
            this.#fileShareService.getTypeList(),
            this.#fileShareService.getMaxNumFiles()
        ]).subscribe({
            next: ([fileTypeList, maxNumFiles,]) => {
                this.fileTypeList = fileTypeList;
                this.maxNumFiles = maxNumFiles ?? 0;
            },
            error: (error) => {
                this.#logService.error(error);
            }
        });
    }

    download(event: Event, fileInfo: FileShareInfo) {
        event.preventDefault();
        this.#store.dispatch(downloadFile({fileInfo}));
    }

    onFilePicked(event: Event) {
        const file = (event.target as HTMLInputElement).files?.[0] ?? null;
        this.uploadForm.patchValue({file: file});
    }

    onUploadFormSubmit() {
        if (this.uploadForm.valid) {
            const file = this.uploadForm.value.file;
            const type = this.uploadForm.value.type;
            if (file != null && type != null) {
                this.#store.dispatch(uploadFile({uploadType: type, file}));
            }
        }
    }
}
